import { yupResolver } from '@hookform/resolvers/yup';
import { AxiosError } from 'axios';
import { useCallback, useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { ArgusLogo, Button, Column, Input, Row } from 'src/common/components';
import { toast } from 'src/common/components/toast';
import { redefinePasswordSchema, useRedefinePassword, useSendForgotPasswordRequest } from 'src/modules/auth';
import { AuthBackground, AuthCard, AuthForm, BackButton, Title } from 'src/modules/auth/components';
import TextWithTimer from 'src/modules/auth/components/forgot-password/TimerLinkText';
import { RedefinePasswordDTO } from 'src/modules/auth/dtos/auth.dto';

export default function RedefinePassword(): JSX.Element {
    const { t, i18n } = useTranslation();

    const controller = useMemo(() => new AbortController(), []);

    const { isLoading, mutateAsync: redefinePassword } = useRedefinePassword({
        onError: error => {
            if (error instanceof AxiosError) {
                return toast.error(
                    t('general.message.error.title'),
                    i18n.exists(error.response?.data?.message) ? t(error.response?.data?.message) : t('general.message.error.message'),
                );
            }
            toast.error(t('general.message.error.title'), t('general.message.error.message'));
        },
        onSuccess: () => {
            toast.success(t('auth.on_confirm_change_pass.success.title'), t('auth.on_confirm_change_pass.success.message'));
            push('/login');
        },
    });
    const { mutateAsync: forgotPassword } = useSendForgotPasswordRequest({
        onError: () => {
            toast.error(t('general.message.error.title'), t('general.message.error.message'));
        },
        onSuccess: () => {
            toast.success(t('auth.on_request_code.success.title'), t('auth.on_request_code.success.message'));
        },
    });

    const [q] = useSearchParams();
    const { pathname } = useLocation();
    const push = useNavigate();

    useEffect(() => {
        const emailData = q.get('email');
        if (!emailData) {
            push('/login');
        }
    }, [pathname, push, q]);

    const {
        register,
        handleSubmit,
        formState: { errors, isValid },
    } = useForm<RedefinePasswordDTO>({
        mode: 'onChange',
        resolver: yupResolver(redefinePasswordSchema),
        defaultValues: {
            code: '',
            email: q.get('email') || '',
            newPassword: '',
            newPasswordConfirmation: '',
        },
    });

    const handlerRequestNewCode = useCallback(async () => {
        const email = q.get('email');

        if (!email) return;

        await forgotPassword({ email, signal: controller.signal });
    }, [controller.signal, forgotPassword, q]);

    const onSubmit = useCallback(
        async (data: RedefinePasswordDTO) => {
            const { email, code, newPassword, newPasswordConfirmation } = data;
            await redefinePassword({
                email,
                code,
                newPassword,
                newPasswordConfirmation,
                signal: controller.signal,
            });
        },
        [controller.signal, redefinePassword],
    );

    useEffect(() => {
        return () => {
            controller.abort();
        };
    }, [controller]);

    return (
        <AuthBackground>
            <Column align="center" justify="center" size="100%">
                <AuthCard>
                    <Row width="100%" align="center" justify="space-between" gap={8}>
                        <BackButton />
                        <ArgusLogo color="#262D3C" height={30} width={131} />
                        <div style={{ width: '30px' }} />
                    </Row>
                    <Column align="center" gap={16}>
                        <Title>{t('auth.forgot_password')}</Title>
                        <AuthForm onSubmit={handleSubmit(onSubmit)}>
                            <Column width="302px" align="center" gap={16}>
                                <Column width="100%" id="fieldset" align="flex-start" gap={8}>
                                    <Input
                                        type="text"
                                        placeholder={t('auth.verification_code')?.toString()}
                                        style={{ width: '100%' }}
                                        feedback={{
                                            message: t(errors['code']?.message || '')?.toString(),
                                        }}
                                        {...register('code')}
                                    />
                                    <Input
                                        type="password"
                                        placeholder={t('auth.new_password')?.toString()}
                                        style={{ width: '100%' }}
                                        feedback={{
                                            message: t(errors['newPassword']?.message || '')?.toString(),
                                        }}
                                        {...register('newPassword')}
                                    />
                                    <Input
                                        type="password"
                                        placeholder={t('auth.confirm_new_password')?.toString()}
                                        style={{ width: '100%' }}
                                        feedback={{
                                            message: t(errors['newPasswordConfirmation']?.message || '')?.toString(),
                                        }}
                                        {...register('newPasswordConfirmation')}
                                    />
                                </Column>
                            </Column>
                            <Button type="submit" disabled={!isValid} isLoading={isLoading}>
                                {t('general.confirm')}
                            </Button>
                            <TextWithTimer
                                enbleFn={t => t <= 0}
                                textOnEnable={t('auth.request_new_code')}
                                onClick={handlerRequestNewCode}
                                mode="countdonw"
                                timer={60}
                            ></TextWithTimer>
                        </AuthForm>
                    </Column>
                </AuthCard>
            </Column>
        </AuthBackground>
    );
}
